import { MonetaryValue, MonetaryValueDataObjectType, MoneyPerAreaValue } from 'ComponentLibrary/src/common/money'
import {
    DeviationAnalysisType,
    DeviationSchema,
    DeviationValueType,
    TransformedDeviationAnalysisType,
    TransformedDeviationSchema,
    YearPeriodDeviationSchema,
} from './types'
import { AreaValue, AreaValueDataObject } from 'ComponentLibrary/src/common/area'
import { some } from 'lodash'
import { Percentage } from 'ComponentLibrary/src/common/percentage'
import { YearPeriod } from 'ComponentLibrary/src/common/year-period'

function deviationSchemaOrUndefined<T extends DeviationValueType>(schema: TransformedDeviationSchema<T>) {
    const hasDeviationValues = [schema.baseValue, schema.comparisonValuation, schema.deviation, schema.deviationPercentage].some(
        (value) => !!value
    )

    return hasDeviationValues ? schema : undefined
}

function transformPercentageDeviationSchema(schema?: DeviationSchema<number>, isInvertedTrend?: boolean) {
    return {
        baseValue: Percentage.PercentageOrUndefined(schema?.base_value),
        comparisonValuation: Percentage.PercentageOrUndefined(schema?.comparison_valuation),
        deviation: Percentage.PercentageOrUndefined(schema?.deviation),
        deviationPercentage: Percentage.PercentageOrUndefined(schema?.deviation_percentage),
        isInvertedTrend,
    }
}

function transformMonetaryDeviationSchema(schema?: DeviationSchema<MonetaryValueDataObjectType>, isInvertedTrend?: boolean) {
    return {
        baseValue: schema?.base_value ? MonetaryValue.fromMonetaryValueDataObject(schema.base_value) : undefined,
        comparisonValuation: schema?.comparison_valuation
            ? MonetaryValue.fromMonetaryValueDataObject(schema.comparison_valuation)
            : undefined,
        deviation: schema?.deviation ? MonetaryValue.fromMonetaryValueDataObject(schema.deviation) : undefined,
        deviationPercentage: Percentage.PercentageOrUndefined(schema?.deviation_percentage),
        isInvertedTrend,
    }
}

function transformMonetaryDeviationSchemaOrUndefined(schema?: DeviationSchema<MonetaryValueDataObjectType>, isInvertedTrend?: boolean) {
    if (schema?.base_value?.amount === 0 && schema?.comparison_valuation?.amount === 0) {
        return undefined
    }
    return deviationSchemaOrUndefined(transformMonetaryDeviationSchema(schema, isInvertedTrend))
}

function transformAreaDeviationSchema(schema?: DeviationSchema<AreaValueDataObject>, isInvertedTrend?: boolean) {
    return {
        baseValue: schema?.base_value ? AreaValue.fromDataObject(schema.base_value) : undefined,
        comparisonValuation: schema?.comparison_valuation ? AreaValue.fromDataObject(schema.comparison_valuation) : undefined,
        deviation: schema?.deviation ? AreaValue.fromDataObject(schema.deviation) : undefined,
        deviationPercentage: Percentage.PercentageOrUndefined(schema?.deviation_percentage),
        isInvertedTrend,
    }
}

function transformNumberDeviationSchema(schema?: DeviationSchema<number>, isInvertedTrend?: boolean) {
    return {
        baseValue: schema?.base_value ?? undefined,
        comparisonValuation: schema?.comparison_valuation ?? undefined,
        deviation: schema?.deviation ?? undefined,
        deviationPercentage: Percentage.PercentageOrUndefined(schema?.deviation_percentage),
        isInvertedTrend,
    }
}

function transformYearPeriodDeviationSchema(schema?: YearPeriodDeviationSchema, isInvertedTrend?: boolean) {
    return {
        baseValue: schema?.base_value ? new YearPeriod(schema.base_value) : undefined,
        comparisonValuation: schema?.comparison_valuation ? new YearPeriod(schema.comparison_valuation) : undefined,
        deviation: schema?.deviation ? new YearPeriod(schema.deviation) : undefined,
        deviationPercentage: null,
        isInvertedTrend,
    }
}

function transformMarketValuePerAreaDeviationSchema(
    schema?: DeviationSchema<MoneyPerAreaValue> & { perAreaLabel: string },
    isInvertedTrend?: boolean
) {
    return {
        customLabel: schema?.perAreaLabel,
        baseValue: schema?.base_value ? MonetaryValue.fromMoneyPerAreaValue(schema?.base_value) : undefined,
        comparisonValuation: schema?.comparison_valuation ? MonetaryValue.fromMoneyPerAreaValue(schema?.comparison_valuation) : undefined,
        deviation: schema?.deviation?.amount ? MonetaryValue.fromMoneyPerAreaValue(schema?.deviation) : undefined,
        deviationPercentage: Percentage.PercentageOrUndefined(schema?.deviation_percentage),
        isInvertedTrend,
    }
}

export function mapDeviationAnalysisSchemaToTable(deviationAnalysis?: DeviationAnalysisType): TransformedDeviationAnalysisType {
    const marketValueResults = {
        marketValue: transformMonetaryDeviationSchema(deviationAnalysis?.market_value),
        mortgageLendingValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.mortgage_lending_value)),
        landValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.land_value)),
        incomeMarketValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.income_market_value)),
        costMarketValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.cost_market_value)),
        comparisonMarketValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.comparison_market_value)),
        transactionCosts: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.transaction_costs, true)),
        totalSpecialPropertyFeatures: transformMonetaryDeviationSchemaOrUndefined(deviationAnalysis?.total_special_property_features),
        perAreaValue: transformMarketValuePerAreaDeviationSchema(deviationAnalysis?.leading_market_value_per_area),
        ancillaryAcquisitionCosts: deviationSchemaOrUndefined(
            transformMonetaryDeviationSchema(deviationAnalysis?.ancillary_acquisition_costs, true)
        ),
    }

    const keyEarningsFigures = {
        grossRentalIncome: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.gross_rental_income)),
        contractualRentalIncome: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.contractual_rental_income)),
        netRentalIncome: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.net_rental_income)),
        netIncomeConstruction: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.net_income_construction)),
        valueConstruction: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.value_construction)),
    }
    const hasKeyEarningsFigures = some(keyEarningsFigures, (value) => value !== undefined)

    const costs = {
        managementCosts: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.management_costs, true)),
        maintenanceCost: transformMonetaryDeviationSchemaOrUndefined(deviationAnalysis?.maintenance_cost, true),
        administrativeCost: transformMonetaryDeviationSchemaOrUndefined(deviationAnalysis?.administrative_cost, true),
        operatingCost: transformMonetaryDeviationSchemaOrUndefined(deviationAnalysis?.operating_cost, true),
        lossOfRentRiskCost: transformMonetaryDeviationSchemaOrUndefined(deviationAnalysis?.loss_of_rent_risk_cost, true),
    }
    const hasCosts = some(costs, (value) => value !== undefined)

    const yieldFigures = {
        grossYieldMultiplier: deviationSchemaOrUndefined(transformNumberDeviationSchema(deviationAnalysis?.gross_yield_multiplier)),
        netYieldMultiplier: deviationSchemaOrUndefined(transformNumberDeviationSchema(deviationAnalysis?.net_yield_multiplier)),
        grossYield: deviationSchemaOrUndefined(transformPercentageDeviationSchema(deviationAnalysis?.gross_yield, true)),
        netYield: deviationSchemaOrUndefined(transformPercentageDeviationSchema(deviationAnalysis?.net_yield, true)),
        grossCapitalValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.gross_capital_value)),
        netCapitalValue: deviationSchemaOrUndefined(transformMonetaryDeviationSchema(deviationAnalysis?.net_capital_value)),
        grossInitialYield: deviationSchemaOrUndefined(transformPercentageDeviationSchema(deviationAnalysis?.gross_initial_yield, true)),
        discountRate: deviationSchemaOrUndefined(transformPercentageDeviationSchema(deviationAnalysis?.discount_rate, true)),
    }
    const hasYieldFigures = some(yieldFigures, (value) => value !== undefined)

    const valuationRelevantAreas = {
        grossFloorArea: deviationSchemaOrUndefined(transformAreaDeviationSchema(deviationAnalysis?.gross_floor_area)),
        rentableArea: deviationSchemaOrUndefined(transformAreaDeviationSchema(deviationAnalysis?.rentable_area)),
    }

    const valuationStructureLifeSpans = Object.fromEntries(
        deviationAnalysis?.structures?.map((structure) => [
            structure.title,
            transformYearPeriodDeviationSchema(structure.life_span_deviation),
        ]) || []
    )

    return {
        marketValueResults,
        keyEarningsFigures: hasKeyEarningsFigures ? keyEarningsFigures : undefined,
        costs: hasCosts ? costs : undefined,
        yieldFigures: hasYieldFigures ? yieldFigures : undefined,
        valuationRelevantAreas,
        valuationStructureLifeSpans,
    }
}
